import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/hooks/use-toast'
import {
  externalCatalogApi,
  ArtistListParams,
  CoverageFilter,
  ReviewPayload,
  SearchParams,
} from '../api/externalCatalogApi'

// ─── Query Keys ──────────────────────────────────────

const PROVIDERS_KEY = 'external-providers'
const ARTISTS_KEY = 'external-artists'
const ARTIST_KEY = 'external-artist'
const ARTWORKS_KEY = 'external-artworks'
const SEARCH_KEY = 'external-search'
const OUR_ARTISTS_KEY = 'external-our-artists'

/** The server's message plus its `detail` when it sent one; else whatever the client knows. */
function describe(error: unknown): string | undefined {
  const data = (error as { response?: { data?: { message?: string; errors?: { detail?: string } } } })
    ?.response?.data
  const parts = [data?.message, data?.errors?.detail].filter(Boolean)
  return parts.length ? parts.join(' — ') : (error as Error)?.message
}

// ─── Reads ───────────────────────────────────────────
//
// No polling. The mirror changes when someone presses Sync or the ingest worker runs; mutations
// invalidate explicitly.

export function useProviders() {
  return useQuery({
    queryKey: [PROVIDERS_KEY],
    queryFn: () => externalCatalogApi.getProviders(),
  })
}

export function useExternalArtists(provider: string, params: ArtistListParams = {}) {
  return useQuery({
    queryKey: [ARTISTS_KEY, provider, params],
    queryFn: () => externalCatalogApi.getArtists(provider, params),
    enabled: !!provider,
  })
}

export function useExternalArtist(provider: string, externalId: string | undefined) {
  return useQuery({
    queryKey: [ARTIST_KEY, provider, externalId],
    queryFn: () => externalCatalogApi.getArtist(provider, externalId!),
    enabled: !!provider && !!externalId,
  })
}

export function useExternalArtworks(
  provider: string,
  externalId: string | undefined,
  coverage: CoverageFilter,
) {
  return useQuery({
    queryKey: [ARTWORKS_KEY, provider, externalId, coverage],
    queryFn: () => externalCatalogApi.getArtworks(provider, externalId!, coverage),
    enabled: !!provider && !!externalId,
  })
}

/**
 * Live search of the vendor's catalog. Disabled until there is something to search for, so
 * opening the tab does not spend a vendor request on an empty query.
 */
export function useCatalogSearch(provider: string, params: SearchParams, enabled: boolean) {
  return useQuery({
    queryKey: [SEARCH_KEY, provider, params],
    queryFn: () => externalCatalogApi.search(provider, params),
    enabled: !!provider && enabled,
    retry: false,
  })
}

export function useOurArtists(q: string) {
  return useQuery({
    queryKey: [OUR_ARTISTS_KEY, q],
    queryFn: () => externalCatalogApi.findOurArtists(q),
    enabled: q.trim().length >= 2,
  })
}

// ─── Writes ──────────────────────────────────────────

export function useSyncArtists(provider: string) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: () => externalCatalogApi.syncArtists(provider),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [PROVIDERS_KEY] })
      queryClient.invalidateQueries({ queryKey: [ARTISTS_KEY] })
      toast({
        title: `Synced ${data.artists.toLocaleString()} artists`,
        description: `${data.created.toLocaleString()} new · ${data.suggested.toLocaleString()} possible matches to our artists`,
      })
    },
    onError: (error: unknown) => {
      toast({ title: 'Sync failed', description: describe(error), variant: 'destructive' })
    },
  })
}

export function useSyncArtist(provider: string, externalId: string) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: () => externalCatalogApi.syncArtist(provider, externalId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [ARTIST_KEY, provider, externalId] })
      queryClient.invalidateQueries({ queryKey: [ARTWORKS_KEY, provider, externalId] })
      queryClient.invalidateQueries({ queryKey: [ARTISTS_KEY] })
      queryClient.invalidateQueries({ queryKey: [PROVIDERS_KEY] })
      toast({
        title: `Synced ${data.artworks.toLocaleString()} artworks`,
        // An incomplete pass is not a failure, but saying "synced" alone would hide that the
        // coverage figures are a floor.
        description: data.complete
          ? `${data.created.toLocaleString()} new · ${data.matched.toLocaleString()} matched to ours`
          : 'This artist has more work than one pass fetches. Sync again to continue.',
      })
    },
    onError: (error: unknown) => {
      toast({ title: 'Sync failed', description: describe(error), variant: 'destructive' })
    },
  })
}

function useArtistMutation<TVars>(
  provider: string,
  externalId: string,
  mutationFn: (vars: TVars) => Promise<unknown>,
  title: (vars: TVars) => string,
  failure: string,
) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn,
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: [ARTIST_KEY, provider, externalId] })
      queryClient.invalidateQueries({ queryKey: [ARTWORKS_KEY, provider, externalId] })
      queryClient.invalidateQueries({ queryKey: [ARTISTS_KEY] })
      queryClient.invalidateQueries({ queryKey: [PROVIDERS_KEY] })
      toast({ title: title(vars) })
    },
    onError: (error: unknown) => {
      toast({ title: failure, description: describe(error), variant: 'destructive' })
    },
  })
}

export function useLinkArtist(provider: string, externalId: string) {
  return useArtistMutation(
    provider,
    externalId,
    (artistId: number) => externalCatalogApi.link(provider, externalId, artistId),
    () => 'Artist linked',
    'Could not link',
  )
}

export function useUnlinkArtist(provider: string, externalId: string) {
  return useArtistMutation(
    provider,
    externalId,
    (reject: boolean) => externalCatalogApi.unlink(provider, externalId, reject),
    (reject) => (reject ? 'Suggestion dismissed' : 'Link removed'),
    'Could not update the link',
  )
}

export function useReviewArtist(provider: string, externalId: string) {
  return useArtistMutation(
    provider,
    externalId,
    (payload: ReviewPayload) => externalCatalogApi.review(provider, externalId, payload),
    () => 'Saved',
    'Could not save',
  )
}

export function useQueueArtworks(provider: string, externalId: string) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (externalIds: string[]) => externalCatalogApi.queue(provider, externalIds),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: [ARTIST_KEY, provider, externalId] })
      queryClient.invalidateQueries({ queryKey: [ARTWORKS_KEY, provider, externalId] })
      queryClient.invalidateQueries({ queryKey: [PROVIDERS_KEY] })
      const skipped = data.requested - data.queued.length
      toast({
        title: `Queued ${data.queued.length} for ingest`,
        description: [
          skipped > 0 ? `${skipped} skipped (already held, in flight or expired).` : null,
          // Queued is not downloading. Say so where the worker cannot run, or the queue looks stuck.
          data.downloadsEnabled ? null : 'Downloads are disabled in this environment, so nothing will be fetched yet.',
        ]
          .filter(Boolean)
          .join(' ') || undefined,
      })
    },
    onError: (error: unknown) => {
      toast({ title: 'Could not queue', description: describe(error), variant: 'destructive' })
    },
  })
}

export function useResolveCandidate(provider: string, externalId: string) {
  return useArtistMutation(
    provider,
    externalId,
    ({ artworkExternalId, held }: { artworkExternalId: string; held: boolean }) =>
      externalCatalogApi.resolve(provider, artworkExternalId, held),
    ({ held }) => (held ? 'Marked as a piece we hold' : 'Marked as a different piece'),
    'Could not save',
  )
}

export function useUnqueueArtworks(provider: string, externalId: string) {
  return useArtistMutation(
    provider,
    externalId,
    (externalIds: string[]) => externalCatalogApi.unqueue(provider, externalIds),
    () => 'Removed from the queue',
    'Could not unqueue',
  )
}
